import { Box, Flex, Grid, Text, Tooltip } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  faChevronLeft,
  faChevronRight,
  faFile,
  faFileLines,
  faInfoCircle,
  faSave
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Banner,
  Button,
  ButtonText,
  Checkbox,
  OutlineCard,
  FormFeedback,
  useToast,
  Table,
  ButtonIcon,
  Link
} from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { useCreateSubmission } from 'src/api/commands';
import { useGenerateConsolidatedPdf } from 'src/api/mutations';
import { fetchConsolidatedPdfStatus } from 'src/api/queries';
import { ISubmissionFormData, useFormContext } from 'src/contexts/FormContext';
import { getFieldConfig } from './question-components/utils';
import StepHeader from './components/StepHeader';
import RinggoldBadge from 'src/components/RinggoldBadge';
import {
  FormFieldAuthors,
  FormFieldCheckbox,
  FormFieldDefinition
} from '@publishing-engine/domain/types/article';
import { FiguresAndTablesDisplay } from 'src/components/Submission/GenericFieldComponents/FiguresAndTables/FiguresAndTableDisplay';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { openFile } from 'src/utils/file';
import { useQuery } from '@tanstack/react-query';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { ensureAllUsers, fetchSubmissionContext } from 'src/api/queries';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';
import DeleteDraft from './DeleteDraft';
import { format } from 'date-fns';
import FiguresDisplay from '../../../../../components/Submission/GenericFieldComponents/Figures/FiguresDisplay';
import { FileListEmptyState } from 'src/components/Submission/GenericFieldComponents/FileList/FileListEmptyState';
import { IPreviewStepConfig } from 'src/hooks/useSubmissionStepsConfig';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { SubmissionSidebar } from './SubmissionSidebar';
import AuthorIntegrityChecks from 'src/components/Submission/AuthorIntegrityChecks/AuthorIntegrityChecks';
import { useAuthorIntegrityChecks } from 'src/components/Submission/AuthorIntegrityChecks/useAuthorIntegrityChecks';
import { FieldLabel } from 'src/components/Field';
import React from 'react';
import {
  IAuthorWithContribution,
  IReviewerRecommendation
} from '@publishing-engine/domain/models';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';
import TablesDisplay from 'src/components/Submission/GenericFieldComponents/Tables/TablesDisplay';
import { getFileListCount } from 'src/utils/fileCount';
import FileViewerButtonText from 'src/components/Submission/FileViewerButtonText';
import GraphicalAbstractImageDisplay from 'src/components/Submission/GenericFieldComponents/GraphicalAbstractImage/GraphicalAbstractImageDisplay';
import { useSubmissionLeaveGuardRegistration } from './SubmissionLeaveGuard';
import ConsolidatedSubmissionFiles from './components/ConsolidatedSubmissionFiles';
import { getSourceFileSetSignature } from './fileSetSignature';
import { getConsolidatedPdfErrorMessage } from './consolidatedPdfError';

interface Props {
  currentStep: number;
  totalSteps: number;
  draftSubmissionId: string;
  setSubmissionId: (submissionId: string) => void;
  setManuscriptId: (manuscriptId: string) => void;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  saveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string
  ) => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId?: string,
    draftStep?: number
  ) => Promise<{
    response: {
      isSuccess: boolean;
      hasRunExtraction?: boolean;
      draft?: Partial<ISubmissionFormData>;
    };
    data: {
      submissionId: string;
    };
  }>;
  stepConfigs: unknown;
  goToStep: (step: number) => void;
}
export interface FormType extends Record<string, FormFieldDefinition | null> {
  SubmissionAgreementConfirmed: FormFieldCheckbox<'metadata.submissionAgreementConfirmed'> | null;
  PublicationConsentConfirmed: FormFieldCheckbox<'metadata.publicationConsentConfirmed'> | null;
  PrivacyPolicyConsentConfirmed: FormFieldCheckbox<'metadata.privacyPolicyConsentConfirmed'> | null;
}

const Divider = (
  <Box
    borderTopWidth="1px"
    borderStyle="solid"
    borderColor="neutralPrimaryBorder"
    width="100%"
  />
);

const PreviewStep: React.FC<Props> = ({
  goToPreviousStep,
  goToNextStep,
  setSubmissionId,
  setManuscriptId,
  saveDraftSubmission,
  currentStep,
  totalSteps,
  draftSubmissionId,
  autosaveDraftSubmission,
  stepConfigs,
  goToStep
}) => {
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [consolidatedPdfStatus, setConsolidatedPdfStatus] = useState<
    'generating' | 'refreshing' | 'complete' | 'error' | null
  >(null);
  const [consolidatedPdfError, setConsolidatedPdfError] = useState<string | null>(
    null
  );
  const [isConsolidatedPdfRequiredOpen, setIsConsolidatedPdfRequiredOpen] =
    useState(false);
  const [
    isConsolidatedPdfAcknowledgementRequiredOpen,
    setIsConsolidatedPdfAcknowledgementRequiredOpen
  ] = useState(false);
  const [isConsolidatedPdfAcknowledged, setIsConsolidatedPdfAcknowledged] =
    useState(false);
  const [
    openConsolidatedPdfPreviewForAcknowledgement,
    setOpenConsolidatedPdfPreviewForAcknowledgement
  ] = useState(false);
  const toast = useToast();
  const {
    formData,
    resetFormData,
    updateFormData,
    consolidatedPdfSourceFileSignature,
    setConsolidatedPdfSourceFileSignature
  } = useFormContext();
  const configs = stepConfigs as IPreviewStepConfig;
  const formFieldData = formData.submissionContext.schema['formFields'];
  const fileCategoryLabels: Record<string, string> = {};
  formFieldData?.forEach(field => {
    const label = field.params?.labels?.main;
    if (typeof label === 'string' && label.trim()) {
      fileCategoryLabels[field.name] = label.trim();
    }
  });

  const hasRunExtraction = formData.hasRunExtraction;

  if (!formFieldData) return <h1>missing form fields</h1>;

  const ctxId = useCurrentContextId() ?? '';
  const { data: focusIssueData } = useQuery({
    queryKey: ['submission-context', ctxId],
    queryFn: () => (ctxId ? fetchSubmissionContext(ctxId) : null)
  });
  const getLabelById = (id: string) => {
    const option =
      focusIssueData?.data?.focusIssues?.filter(item => item.id === id) ?? [];
    return option.length > 0 ? option[0]?.name : '';
  };

  const formConfigs: FormType = {
    SubmissionAgreementConfirmed: getFieldConfig(
      formFieldData,
      'SubmissionAgreementConfirmed'
    ),
    PublicationConsentConfirmed: getFieldConfig(
      formFieldData,
      'PublicationConsentConfirmed'
    ),
    PrivacyPolicyConsentConfirmed: getFieldConfig(
      formFieldData,
      'PrivacyPolicyConsentConfirmed'
    )
  };
  const schema = generateValidationSchema(formConfigs);

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    getValues,
    setValue
  } = useForm<ISubmissionFormData>({
    defaultValues: formData,
    resolver: ajvResolver(schema)
  });

  const { submissionContext } = useSubmissionContextData();

  // The checks this journal expects an author to run before submitting.
  const configuredIntegrityChecks =
    submissionContext?.config.integrityChecks
      ?.find(config => config.event === 'SubmissionCreated')
      ?.allowedChecks?.find(allowed => allowed.role === 'author')?.checks ?? [];

  const integrity = useAuthorIntegrityChecks(
    draftSubmissionId,
    configuredIntegrityChecks
  );
  const [hasBlockedIntegritySubmit, setHasBlockedIntegritySubmit] =
    useState(false);
  const integrityCardRef = useRef<HTMLDivElement>(null);

  const createSubmissionMutation = useCreateSubmission(
    formData.submissionContext
  );

  const generateConsolidatedPdfMutation = useGenerateConsolidatedPdf();

  const hasGeneratedConsolidatedPdfs = Boolean(
    formData.fileSet?.ConsolidatedPdf &&
      formData.fileSet?.AnonymizedConsolidatedPdf
  );

  useEffect(() => {
    if (
      hasGeneratedConsolidatedPdfs &&
      consolidatedPdfSourceFileSignature === null
    ) {
      setConsolidatedPdfSourceFileSignature(
        getSourceFileSetSignature(formData.fileSet)
      );
    }
  }, [
    consolidatedPdfSourceFileSignature,
    formData.fileSet,
    hasGeneratedConsolidatedPdfs,
    setConsolidatedPdfSourceFileSignature
  ]);

  const currentSourceFileSetSignature = getSourceFileSetSignature(
    formData.fileSet
  );
  const hasConsolidatedPdfSourceChanges = Boolean(
    hasGeneratedConsolidatedPdfs &&
      (formData.isFileUpdated === true ||
        (consolidatedPdfSourceFileSignature !== null &&
          consolidatedPdfSourceFileSignature !== currentSourceFileSetSignature))
  );

  const reviewSubmissionInformation =
    submissionContext?.config.cms.authorSubmissionForm
      .reviewSubmissionInformation;
  const reviewAuthorInformation =
    submissionContext?.config.cms.authorSubmissionForm.reviewAuthorInformation;
  const submissionPreferences =
    submissionContext?.config.cms.authorSubmissionForm.submissionPreferences;
  const submissionInformationTitle =
    reviewSubmissionInformation?.header?.trim() || 'Submission Information';
  const authorInformationTitle =
    reviewAuthorInformation?.header?.trim() || 'Author Information';
  const submissionPreferencesTitle =
    submissionPreferences?.header?.trim() || 'Submission Preferences';

  const onSaveDraft = () => {
    saveDraftSubmission(formData, draftSubmissionId);
  };

  const saveCurrentProgress = useCallback(async () => {
    const values = getValues();
    updateFormData(values);
    const results = await autosaveDraftSubmission(
      values,
      draftSubmissionId,
      currentStep + 2
    );

    if (results.response.isSuccess) {
      const { draft, hasRunExtraction } = results.response;
      const extractionStatus =
        typeof hasRunExtraction === 'boolean' ? { hasRunExtraction } : {};

      if (draft) {
        updateFormData({ ...draft, ...extractionStatus });
      } else if (hasRunExtraction !== undefined) {
        updateFormData(extractionStatus);
      }
    }

    return results.response.isSuccess;
  }, [
    autosaveDraftSubmission,
    currentStep,
    draftSubmissionId,
    getValues,
    updateFormData
  ]);

  useSubmissionLeaveGuardRegistration({
    hasUnsavedChanges: isDirty,
    saveCurrentProgress
  });

  const generateConsolidatedPdf = async (rerun = false) => {
    setConsolidatedPdfError(null);
    if (rerun) {
      setIsConsolidatedPdfAcknowledged(false);
    }
    setConsolidatedPdfStatus('generating');
    if (!draftSubmissionId) {
      setConsolidatedPdfStatus('error');
      setConsolidatedPdfError('Please save your draft before generating the consolidated PDF.');
      toast({
        variant: 'error',
        title: 'No draft found',
        description:
          'Please save your draft before generating the consolidated PDF.',
        isClosable: true
      });
      return;
    }

    try {
      const result = await generateConsolidatedPdfMutation.mutateAsync({
        submissionContextId: ctxId,
        submissionId: draftSubmissionId,
        rerun
      });

      const generationRequestWasTooLarge =
        !result.isSuccess && 'status' in result && result.status === 413;
      if (!result.isSuccess && !generationRequestWasTooLarge) {
        const errorMessage = getConsolidatedPdfErrorMessage(result.message);
        setConsolidatedPdfStatus('error');
        setConsolidatedPdfError(errorMessage || 'Please try again.');
        toast({
          variant: 'error',
          title: 'Consolidated PDF generation failed',
          description: errorMessage || 'Please try again.',
          isClosable: true
        });
        return;
      }

      setConsolidatedPdfStatus('refreshing');
      if (generationRequestWasTooLarge) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
      let status = await fetchConsolidatedPdfStatus(ctxId, draftSubmissionId);
      while (status.status !== 'completed') {
        if (status.status === 'failed') {
          setConsolidatedPdfStatus('error');
          setConsolidatedPdfError(status.error || 'Please try again.');
          toast({
            variant: 'error',
            title: 'Consolidated PDF generation failed',
            description: status.error || 'Please try again.',
            isClosable: true
          });
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        status = await fetchConsolidatedPdfStatus(ctxId, draftSubmissionId);
      }

      if (status.file && status.anonymizedFile) {
        const updatedFileSet = {
          ...getValues('fileSet'),
          ConsolidatedPdf: status.file,
          AnonymizedConsolidatedPdf: status.anonymizedFile
        };
        setConsolidatedPdfSourceFileSignature(
          getSourceFileSetSignature(updatedFileSet)
        );
        setValue('fileSet', updatedFileSet, { shouldDirty: true });
        setValue('isFileUpdated', false, { shouldDirty: true });
        updateFormData({
          fileSet: updatedFileSet,
          isFileUpdated: false
        });
      }
      setConsolidatedPdfStatus('complete');
      toast({
        variant: 'success',
        title: 'Consolidated PDF generated',
        description: 'The consolidated PDF files are ready.',
        isClosable: true
      });
    } catch (error) {
      const errorMessage = getConsolidatedPdfErrorMessage(error);
      setConsolidatedPdfStatus('error');
      setConsolidatedPdfError(errorMessage || 'Please try again.');
      toast({
        variant: 'error',
        title: 'Consolidated PDF generation failed',
        description: errorMessage || 'Please try again.',
        isClosable: true
      });
    }
  };

  const closeConsolidatedPdfStatus = () => setConsolidatedPdfStatus(null);

  const submitForm = async () => {
    const currentFileSet = getValues('fileSet');
    if (
      submissionContext?.config.enableConsolidatedPdf &&
      hasConsolidatedPdfSourceChanges
    ) {
      document
        .getElementById('consolidated-file-changes-banner')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (
      submissionContext?.config.enableConsolidatedPdf &&
      (!currentFileSet?.ConsolidatedPdf ||
        !currentFileSet?.AnonymizedConsolidatedPdf)
    ) {
      setIsConsolidatedPdfRequiredOpen(true);
      return;
    }
    if (
      submissionContext?.config.enableConsolidatedPdf &&
      !isConsolidatedPdfAcknowledged
    ) {
      setIsConsolidatedPdfAcknowledgementRequiredOpen(true);
      return;
    }

    // Prevent multiple clicks
    if (isNavigating) return;

    // A configured check must have run and must not have found a risk before
    // the submission can be created. Send the author to the panel either way:
    // it is where the findings and the run action are. An unreadable result set
    // does not hold the submission back — see `blocksSubmit`.
    if (integrity.blocksSubmit) {
      setHasBlockedIntegritySubmit(true);
      integrityCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      if (integrity.gate === 'not-run') {
        toast({
          variant: 'error',
          title: 'Integrity checks were not executed.',
          description:
            'Make sure to perform integrity checks prior to submitting your work.',
          isClosable: true
        });
      } else if (integrity.gate === 'running') {
        toast({
          variant: 'attention',
          title: 'Integrity checks are still running.',
          description: 'Wait for the results before submitting your work.',
          isClosable: true
        });
      }
      // Risks need no toast: the panel's Action Required banner is the message.

      return;
    }

    setIsNavigating(true);

    try {
      const values = getValues();
      const sanitizedFileSet =
        configs.TitlePage && values.fileSet?.TitlePage?.url
          ? values.fileSet
          : (({ TitlePage: _TitlePage, ...fileSet }) => fileSet)(
              values.fileSet
            );
      const sanitizedValues = {
        ...values,
        fileSet: sanitizedFileSet
      };
      updateFormData(sanitizedValues);

      const correspondingAuthorEmail = formData.correspondingAuthor;
      let updatedAuthors = sanitizedValues.metadata.authors;

      try {
        const coAuthors = formData.metadata.authors;
        const coAuthorsEmails = coAuthors.map(author => author.email);
        const usersResponse = await ensureAllUsers(coAuthorsEmails);
        const responseMap = new Map(
          usersResponse.map(user => [user.email, user.userId])
        );

        updatedAuthors = updatedAuthors.map(author => ({
          ...author,
          email: author.email.toLowerCase(),
          id: responseMap.get(author.email.toLowerCase())
        }));
      } catch (_error) {
        toast({
          variant: 'attention',
          title: 'Co Author Invite Failed',
          description: 'Not all co authors were invited at this time.',
          isClosable: true
        });
      }

      const response = await createSubmissionMutation.mutateAsync({
        ...sanitizedValues,
        correspondingAuthor: correspondingAuthorEmail,
        metadata: {
          ...sanitizedValues.metadata,
          authors: updatedAuthors
        },
        draftSubmissionId
      });

      if (response.isSuccess) {
        setSubmissionId(response.submissionId);
        setManuscriptId(response.manuscriptId ?? '');
      }
      resetFormData();
      goToNextStep();
    } finally {
      setIsNavigating(false);
    }
  };

  const publishingModel =
    configs.PublishingModel?.params.labels.options[
      formData.preference.publishingModel?.model ?? ''
    ];

  const { submittingAuthor, contactAuthor, correspondingAuthor } = formData;
  const authorRoleMap: IAuthorRolesMap = {
    Submitting: submittingAuthor,
    ...(contactAuthor && {
      Contact: contactAuthor
    }),
    ...(correspondingAuthor && {
      Corresponding: correspondingAuthor
    })
  };

  const figures = formData?.fileSet?.Figures ?? [];
  const hasFigures = figures.length > 0;
  const graphicalAbstractImages =
    formData?.fileSet?.GraphicalAbstractImage ?? [];
  const hasGraphicalAbstractImages = graphicalAbstractImages.length > 0;

  const tables = formData?.fileSet?.Tables ?? [];
  const hasTables = tables.length > 0;

  const supplementaryFileCount = getFileListCount(
    formData.fileSet.Supplementary
  );
  const additionalSupplementaryFileCount = getFileListCount(
    formData.fileSet.AdditionalSupplementary
  );

  const standardReportingGuidelinesFileCount =
    getFileListCount(formData.fileSet.StandardReportingGuidelinesFileDetails) +
    getFileListCount(formData.fileSet.CONSORTFiles) +
    getFileListCount(formData.fileSet.ARRIVEFiles);

  const authorConfig = configs?.Authors as FormFieldAuthors;
  const { orcidField, affiliationField, contributionsField } =
    authorConfig?.params.validations ?? {};
  const renderReviewerCard = (
    reviewer: IReviewerRecommendation,
    index: number
  ) => (
    <Box
      key={index}
      border="1px solid"
      borderColor="neutralPrimaryBorder"
      borderRadius="md"
      p={3}
      background="white"
    >
      <Flex gap={2} mb={1} alignItems="center">
        <Text textStyle="textContext" color="GrayText">
          #{index + 1}
        </Text>
        <Text textStyle="headingLine">{`${reviewer.firstName} ${reviewer.lastName}`}</Text>
      </Flex>
      <Text textStyle="textContext" mb={2} wordBreak="break-word">
        {reviewer.email}
      </Text>

      <Text textStyle="headingItem" fontSize="xs" color="gray.500" mb={1}>
        Affiliation
      </Text>
      <Flex gap={2} alignItems="flex-start" mb={2}>
        {reviewer.ringgoldId && <RinggoldBadge />}
        <Text textStyle="textBody">{`${reviewer.affiliation}, ${reviewer.country}`}</Text>
      </Flex>

      <Text textStyle="headingItem" fontSize="xs" color="gray.500" mb={1}>
        Reason for Recommendation
      </Text>
      <Text textStyle="textBody">{reviewer.reason}</Text>
    </Box>
  );

  const renderAuthorCard = (author: IAuthorWithContribution, index: number) => {
    const hasName =
      [author.firstName, author.middleName, author.lastName].filter(Boolean)
        .length > 0;
    return (
      <Box
        key={index}
        border="1px solid"
        borderColor="neutralPrimaryBorder"
        borderRadius="md"
        p={3}
        background="white"
        data-cy="author-card"
      >
        <Flex gap={2} mb={2} alignItems="center">
          <Text textStyle="textContext" color="GrayText">
            #{index + 1}
          </Text>
          {hasName ? (
            <Text textStyle="actionLabel" data-cy="author-name">
              {[author.firstName, author.middleName, author.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>
          ) : (
            <Text
              color="GrayText"
              textStyle="actionLabel"
              data-cy="no-name-data"
            >
              No Name Data
            </Text>
          )}
        </Flex>

        {author.email ? (
          <Text
            textStyle="textContext"
            mb={1}
            data-cy="author-email"
            wordBreak="break-word"
          >
            {author.email}
          </Text>
        ) : (
          <Text
            color="GrayText"
            textStyle="textContext"
            mb={1}
            data-cy="no-email-data"
          >
            No email address provided
          </Text>
        )}

        {orcidField !== 'hidden' && (
          <>
            {author.orcid ? (
              <Flex alignItems="center" flexDirection="row" gap={1} mb={1}>
                <OrcidIDIcon value={author.orcid} />
                <Text textStyle="textContext">{author.orcid}</Text>
              </Flex>
            ) : (
              <Text
                color={
                  orcidField === 'mandatory'
                    ? 'actionSecondaryCautionContent'
                    : 'GrayText'
                }
                textStyle="textContext"
                mb={1}
              >
                No ORCID provided
              </Text>
            )}
          </>
        )}

        <Flex
          gap={2}
          flexWrap="wrap"
          mb={
            affiliationField !== 'hidden' || contributionsField !== 'hidden'
              ? 2
              : 0
          }
        >
          {Object.keys(authorRoleMap).map(role => {
            if (authorRoleMap[role as keyof IAuthorRolesMap] === author.email) {
              return (
                <Badge key={role} variant="attention" size="context">
                  {role} Author
                </Badge>
              );
            }
          })}
        </Flex>

        {affiliationField !== 'hidden' && (
          <Flex
            flexDirection="column"
            gap={1}
            mb={contributionsField !== 'hidden' ? 2 : 0}
          >
            <Text textStyle="headingItem" fontSize="xs" color="gray.500">
              Affiliation
            </Text>
            <Flex gap={2} alignItems="flex-start" data-cy="affiliation-cell">
              {author.ringgoldId && <RinggoldBadge data-cy="ringgold-badge" />}
              {author.affiliation ? (
                <Text textStyle="textBody" data-cy="author-affiliation">
                  {author.affiliation}
                  {author.city && `, ${author.city}`}
                  {author.country && `, ${author.country}`}
                  {author.postalCode && `, ${author.postalCode}`}
                </Text>
              ) : (
                <Text
                  color={
                    affiliationField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : 'GrayText'
                  }
                  textStyle="textBody"
                  data-cy="no-affiliation-data"
                >
                  No Affiliation provided
                </Text>
              )}
            </Flex>
          </Flex>
        )}

        {contributionsField !== 'hidden' && (
          <Flex flexDirection="column" gap={1}>
            <Text textStyle="headingItem" fontSize="xs" color="gray.500">
              CRediT Contributions
            </Text>
            <Box data-cy="contributions-cell">
              <ContributionsBadge
                contributions={author.contributions}
                isMandatory={contributionsField === 'mandatory'}
              />
            </Box>
          </Flex>
        )}
      </Box>
    );
  };

  const renderAuthorTableRow = (
    author: IAuthorWithContribution,
    index: number
  ) => {
    const hasName =
      [author.firstName, author.middleName, author.lastName].filter(Boolean)
        .length > 0;
    return (
      <Table.Row
        alignItems="start"
        _last={{ borderBottom: 'none' }}
        data-cy="author-row"
      >
        <Table.RowCell width="80px">{index + 1}</Table.RowCell>
        <Table.RowCell
          flex={1}
          gap={2}
          display="flex"
          flexDirection="column"
          data-cy="author-cell"
        >
          {hasName ? (
            <Text textStyle="actionLabel" data-cy="author-name">
              {[author.firstName, author.middleName, author.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>
          ) : (
            <Text
              color="GrayText"
              textStyle="actionLabel"
              data-cy="no-name-data"
            >
              No Name Data
            </Text>
          )}
          {author.email ? (
            <Text
              textStyle="textContext"
              noOfLines={1}
              overflow="hidden"
              textOverflow="ellipsis"
              data-cy="author-email"
            >
              {author.email}
            </Text>
          ) : (
            <Text
              color="GrayText"
              textStyle="textContext"
              data-cy="no-email-data"
            >
              No email address provided
            </Text>
          )}

          {orcidField !== 'hidden' && (
            <>
              {author.orcid ? (
                <Flex alignItems="center" flexDirection="row" gap={1}>
                  <OrcidIDIcon value={author.orcid} />
                  <Text textStyle="textContext">{author.orcid}</Text>
                </Flex>
              ) : (
                <Text
                  color={
                    orcidField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : 'GrayText'
                  }
                  textStyle="textContext"
                >
                  No ORCID provided
                </Text>
              )}
            </>
          )}

          <Flex gap={4}>
            {Object.keys(authorRoleMap).map(role => {
              if (
                authorRoleMap[role as keyof IAuthorRolesMap] === author.email
              ) {
                return (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                );
              }
            })}
          </Flex>
        </Table.RowCell>

        {affiliationField !== 'hidden' && (
          <Table.RowCell
            flex={1}
            gap={2}
            display="flex"
            flexDirection="row"
            alignItems="flex-start"
            data-cy="affiliation-cell"
          >
            {author.ringgoldId && <RinggoldBadge data-cy="ringgold-badge" />}
            {author.affiliation ? (
              <Text textStyle="textBody" data-cy="author-affiliation">
                {author.affiliation}
                {author.city && `, ${author.city}`}
                {author.country && `, ${author.country}`}
                {author.postalCode && `, ${author.postalCode}`}
              </Text>
            ) : (
              <Text
                color={
                  affiliationField === 'mandatory'
                    ? 'actionSecondaryCautionContent'
                    : ''
                }
                textStyle="textBody"
                data-cy="no-affiliation-data"
              >
                No Affiliation provided
              </Text>
            )}
          </Table.RowCell>
        )}

        {contributionsField !== 'hidden' && (
          <Table.RowCell
            flex={1}
            gap={2}
            display="flex"
            flexDirection="row"
            data-cy="contributions-cell"
          >
            <ContributionsBadge
              contributions={author.contributions}
              isMandatory={contributionsField === 'mandatory'}
            />
          </Table.RowCell>
        )}
      </Table.Row>
    );
  };

  const showAcceleratedPublication =
    configs.AcceleratedPublication &&
    formData.preference.acceleratedPublication;
  const showIPEMMembership =
    configs.IPEMMembership && formData.preference.ipemMembership;
  const showSubjectArea =
    configs.SubjectArea && formData.preference.subjectArea;

  const showAdditionalPreferences =
    showAcceleratedPublication || showIPEMMembership || showSubjectArea;

  const hasSubmissionPreferencesData = Boolean(
    Object.keys(formData?.preference)?.length
  );

  const reproducedContentExternalDependency =
    configs.LicensesAndPermissionsReproducedContent?.params
      .externalFieldDependency;
  const permissionFilesExternalDependency =
    configs.LicensesAndPermissionsFiles?.params.externalFieldDependency;

  return (
    <form
      onSubmit={event => {
        if (event.target === event.currentTarget) {
          void handleSubmit(submitForm)(event);
        }
      }}
    >
      <SidebarLayout
        sidebar={
          <SubmissionSidebar
            currentStep={currentStep}
            enableConsolidatedPdf={
              submissionContext?.config.enableConsolidatedPdf
            }
            hasIntegrityChecks={configuredIntegrityChecks.length > 0}
            onNavigate={step => {
              goToStep(step);
            }}
          />
        }
      >
        <StepHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          heading="Preview Submission"
          description="Please review all the details of your submission carefully. Ensure that every piece of information is accurate and complete before proceeding. Once you are confident that everything is correct, you may proceed to submit your work."
        />

        {!hasRunExtraction && (
          <Banner
            variant="attention"
            heading="Files Replaced - Extraction Not Updated"
            content="You replaced the manuscript and title page files, but have not re-run the extraction process. The preview may still show the original files. Please return to the submission step and re-run extraction to ensure the preview reflects your latest files."
          />
        )}
        <AuthorIntegrityChecks
          integrity={integrity}
          submissionId={draftSubmissionId}
          submissionContextId={ctxId}
          showNotRunError={hasBlockedIntegritySubmit}
          containerRef={integrityCardRef}
        />
        <Flex gap={4} flexDirection="column" mt={4}>
          {/* ----- Submission ----- */}
          {(configs.Title || configs.Abstract || configs.Keywords) && (
            <>
              <Flex>
                <Text textStyle="headingGroup" id={submissionInformationTitle}>
                  {submissionInformationTitle}
                </Text>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label="Confirms all essential manuscript metadata, files, and category selections are complete and ready for final submission"
                  borderRadius={4}
                  data-cy="contributions-info-tooltip"
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="Back to workspace settings"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    data-cy="contributions-info-icon"
                  />
                </Tooltip>
              </Flex>
              <OutlineCard width="100%" padding={4}>
                <Flex flexDirection="column" alignItems="flex-start" gap={4}>
                  <Flex flexDirection="column" gap={2}>
                    <FieldLabel
                      isRequired={true}
                      label={'Manuscript Type'}
                      subText=""
                      data-cy="manuscript-type-text"
                    />
                    <Flex>
                      <Badge variant="attention" size="context">
                        <Flex
                          gap={1}
                          alignItems="center"
                          data-cy="submission-type"
                        >
                          <FontAwesomeIcon icon={faFileLines} />
                          {formData.type}
                        </Flex>
                      </Badge>
                    </Flex>
                  </Flex>

                  <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                    gap={4}
                  >
                    {configs.ArticleSubtype && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.ArticleSubtype.params.validations.required
                          }
                          label={configs.ArticleSubtype.params.labels.main}
                          subText=""
                        />
                        {formData.metadata.articleSubtype ? (
                          <Text
                            textStyle="textBody"
                            width={'100%'}
                            data-cy="articleSubtype"
                          >
                            {formData.metadata.articleSubtype}
                          </Text>
                        ) : (
                          <Flex wrap={'wrap'}>
                            <Badge
                              variant="neutral"
                              whiteSpace="pre-wrap"
                              data-cy="badge-articleSubtype"
                            >
                              <Text
                                textStyle="textContext"
                                data-cy="articleLanguage"
                              >
                                N/A
                              </Text>
                            </Badge>
                          </Flex>
                        )}
                      </Flex>
                    )}

                    {configs.ArticleLanguage && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.ArticleLanguage.params.validations.required
                          }
                          label={configs.ArticleLanguage.params.labels.main}
                          subText=""
                        />

                        <Flex gap={2} wrap={'wrap'}>
                          <Badge
                            variant="neutral"
                            data-cy={`badge-articleLanguage`}
                            whiteSpace="pre-wrap"
                          >
                            {formData.metadata.articleLanguage && (
                              <Text
                                textStyle="textContext"
                                data-cy="articleLanguage"
                              >
                                {formData.metadata.articleLanguage}
                              </Text>
                            )}
                          </Badge>
                        </Flex>
                      </Flex>
                    )}

                    {configs.FocusIssue && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.FocusIssue.params.validations.required
                          }
                          label={configs.FocusIssue.params.labels.main}
                          subText=""
                          data-cy="focus-issue-title-text"
                        />
                        {formData?.preference?.focusIssue ? (
                          <Badge
                            variant="neutral"
                            data-cy={`badge-specialIssue`}
                            whiteSpace="pre-wrap"
                          >
                            <Text textStyle="textContext" data-cy="focus-issue">
                              {getLabelById(
                                formData?.preference?.focusIssue ?? ''
                              )}
                            </Text>
                          </Badge>
                        ) : (
                          <Text textStyle="textContext" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Grid>

                  {configs.Title && (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        isRequired={configs.Title.params.validations.required}
                        label={configs.Title.params.labels.main}
                        subText=""
                        data-cy="abstract-title-text"
                      />
                      <Box textStyle="textBody">
                        <RichTextPreview
                          value={formData.metadata.title}
                          data-cy="preview-title"
                        />
                      </Box>
                    </Flex>
                  )}

                  {configs.EnglishTitle &&
                    formData?.metadata?.articleLanguage !== 'English' && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.EnglishTitle.params.validations.required
                          }
                          label={configs.EnglishTitle.params.labels.main}
                          subText=""
                          data-cy="EnglishTitle-title-text"
                        />
                        <Box>
                          <RichTextPreview
                            value={formData.metadata.englishTitle}
                            data-cy="preview-EnglishTitle"
                          />
                        </Box>
                      </Flex>
                    )}

                  {configs.Subtitle && (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        isRequired={
                          configs.Subtitle.params.validations.required
                        }
                        label={configs.Subtitle.params.labels.main}
                        subText=""
                        data-cy="subtitle-title-text"
                      />
                      <Box>
                        {formData.metadata.subtitle ? (
                          <RichTextPreview
                            value={formData.metadata.subtitle}
                            data-cy="preview-subtitle"
                          />
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Box>
                    </Flex>
                  )}

                  {configs.EnglishSubtitle &&
                    formData?.metadata?.articleLanguage !== 'English' && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.EnglishSubtitle.params.validations.required
                          }
                          label={configs.EnglishSubtitle.params.labels.main}
                          subText=""
                          data-cy="EnglishSubtitle-title-text"
                        />
                        <Box>
                          {formData.metadata.englishSubtitle ? (
                            <RichTextPreview
                              value={formData.metadata.englishSubtitle}
                              data-cy="preview-EnglishSubtitle"
                            />
                          ) : (
                            <Text textStyle="textBody" color="GrayText">
                              No data available
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    )}

                  {configs.Abstract && (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        isRequired={
                          configs.Abstract.params.validations.required
                        }
                        label={configs.Abstract.params.labels.main}
                        subText=""
                        data-cy="abstract-title-text"
                      />
                      <Box>
                        <RichTextPreview
                          value={formData.metadata.abstract}
                          data-cy="preview-abstract"
                        />
                      </Box>
                    </Flex>
                  )}

                  {configs.EnglishAbstract &&
                    formData?.metadata?.articleLanguage !== 'English' && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.EnglishAbstract.params.validations.required
                          }
                          label={configs.EnglishAbstract.params.labels.main}
                          subText=""
                          data-cy="EnglishAbstract-title-text"
                        />
                        {formData.metadata.englishAbstract ? (
                          <Box>
                            <RichTextPreview
                              value={formData.metadata.englishAbstract}
                              data-cy="preview-EnglishAbstract"
                            />
                          </Box>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}

                  <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                    gap={4}
                  >
                    {configs.Manuscript && (
                      <Flex gap={2} flexDirection="column">
                        <FieldLabel
                          isRequired={
                            configs.Manuscript.params.validations.required
                          }
                          label="Manuscript"
                          subText=""
                          data-cy="manuscript-title-text"
                        />
                        <Flex
                          alignItems="center"
                          width="100%"
                          gap={1}
                          data-cy="manuscript-file"
                        >
                          <FileViewerButtonText
                            file={formData.fileSet?.Manuscript}
                            submissionContextId={ctxId}
                            wordBreak={'break-word'}
                            textAlign={'start'}
                            whiteSpace={'normal'}
                            data-cy="button-preview-manuscript"
                          >
                            {formData.fileSet?.Manuscript?.name}
                          </FileViewerButtonText>
                        </Flex>
                      </Flex>
                    )}

                    {configs.TitlePage && (
                      <Flex gap={2} flexDirection="column">
                        <FieldLabel
                          isRequired={
                            configs.TitlePage.params.validations.required
                          }
                          label="Title Page"
                          subText=""
                          data-cy="title-page-title-text"
                        />
                        <Flex
                          alignItems="center"
                          width="100%"
                          gap={1}
                          data-cy="title-page-file"
                        >
                          {formData.fileSet?.TitlePage && (
                            <FileViewerButtonText
                              file={formData.fileSet?.TitlePage}
                              submissionContextId={ctxId}
                              wordBreak={'break-word'}
                              textAlign={'start'}
                              whiteSpace={'normal'}
                              data-cy="button-preview-title-page"
                            >
                              {formData.fileSet?.TitlePage?.name}
                            </FileViewerButtonText>
                          )}
                        </Flex>
                      </Flex>
                    )}
                  </Grid>

                  <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                    gap={4}
                  >
                    {configs.Keywords && (
                      <Flex direction={'column'} gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.Keywords.params.validations.required
                          }
                          label={configs.Keywords.params.labels.main}
                          subText=""
                          data-cy="keywords-title-text"
                        />
                        <Flex gap={2} wrap={'wrap'}>
                          {formData.keywords.map((keyword, index) => (
                            <Badge
                              key={index}
                              variant="neutral"
                              data-cy={`badge-keyword-${index}`}
                              whiteSpace="pre-wrap"
                            >
                              <Text textStyle="textContext" data-cy="keyword">
                                {keyword.text}
                              </Text>
                            </Badge>
                          ))}
                        </Flex>
                      </Flex>
                    )}
                    {configs.EnglishKeywords &&
                      formData?.metadata?.articleLanguage !== 'English' && (
                        <Flex direction={'column'} gap={2}>
                          <FieldLabel
                            isRequired={
                              configs.EnglishKeywords.params.validations
                                .required
                            }
                            label={configs.EnglishKeywords.params.labels.main}
                            subText=""
                            data-cy="keywords-title-text"
                          />
                          <Flex gap={2} wrap={'wrap'}>
                            {formData.englishKeywords.map((keyword, index) => (
                              <Badge
                                key={index}
                                variant="neutral"
                                data-cy={`badge-keyword-${index}`}
                                whiteSpace="pre-wrap"
                              >
                                <Text textStyle="textContext" data-cy="keyword">
                                  {keyword.text}
                                </Text>
                              </Badge>
                            ))}
                          </Flex>
                        </Flex>
                      )}

                    {configs.Category && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.Category.params.validations.required
                          }
                          label={configs.Category.params.labels.main}
                          subText=""
                        />

                        {formData?.preference?.category ? (
                          <Flex gap={2} wrap="wrap">
                            <Badge
                              variant="neutral"
                              data-cy="badge-category"
                              whiteSpace="pre-wrap"
                            >
                              {formData.preference.category !== 'Other' ? (
                                <Text
                                  textStyle="textContext"
                                  data-cy="category"
                                >
                                  {formData.preference.category}
                                </Text>
                              ) : formData.preference.otherCategory ? (
                                <Text
                                  textStyle="textContext"
                                  data-cy="otherCategory"
                                >
                                  {formData.preference.otherCategory}
                                </Text>
                              ) : (
                                <Text textStyle="textBody" color="GrayText">
                                  No data available
                                </Text>
                              )}
                            </Badge>
                          </Flex>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Grid>
                  {configs.SourceFiles &&
                    !!formData.fileSet.SourceFiles?.length && (
                      <Flex gap={2} flexDirection="column">
                        <FieldLabel
                          isRequired={
                            configs.SourceFiles.params.validations.required
                          }
                          label={configs.SourceFiles.params.labels.main}
                          subText=""
                          data-cy="sourcefile-files-title-text"
                        />
                        <Flex flexDirection="column">
                          {formData.fileSet.SourceFiles?.map(file => (
                            <Flex
                              key={file.url}
                              alignItems="center"
                              paddingY={4}
                              width="100%"
                              height="60px"
                              gap={2}
                              data-cy={`sourcefile-file-${file.name}`}
                            >
                              <FileViewerButtonText
                                file={file}
                                submissionContextId={ctxId}
                                wordBreak={'break-word'}
                                textAlign={'start'}
                                whiteSpace={'normal'}
                                data-cy={`button-preview-${file.name}`}
                              >
                                {file.name}
                              </FileViewerButtonText>
                            </Flex>
                          ))}
                        </Flex>
                      </Flex>
                    )}
                  {formData.fileSet?.AnonymizedManuscript?.url && (
                    <Flex gap={2} flexDirection="column">
                      <Text
                        textStyle="headingLine"
                        data-cy="anonymised-manuscript-title-text"
                      >
                        Anonymised Manuscript
                      </Text>
                      <Flex
                        alignItems="center"
                        paddingY={4}
                        width="100%"
                        height="60px"
                        gap={2}
                        data-cy="manuscript-file"
                      >
                        <FileViewerButtonText
                          file={formData.fileSet?.AnonymizedManuscript}
                          submissionContextId={ctxId}
                          wordBreak={'break-word'}
                          textAlign={'start'}
                          whiteSpace={'normal'}
                          data-cy="button-preview-anonymized-manuscript"
                        >
                          {formData.fileSet?.AnonymizedManuscript?.name}
                        </FileViewerButtonText>
                      </Flex>
                    </Flex>
                  )}

                  {configs.StandardReportingGuidelines && (
                    <Flex gap={2} flexDirection="column">
                      <FieldLabel
                        isRequired={
                          configs.StandardReportingGuidelines.params.validations
                            .required
                        }
                        label={`${configs.StandardReportingGuidelines.params.labels.main} (${standardReportingGuidelinesFileCount})`}
                        subText=""
                        data-cy="standard-reporting-guidelines-consent-title-text"
                      />

                      {formData.preference.standardReportingGuidelines ? (
                        <Text
                          textStyle="textBody"
                          data-cy="standard-reporting-guidelines-consent"
                          mb={2}
                        >
                          {
                            configs.StandardReportingGuidelines.params.labels
                              .options['true']?.main
                          }
                        </Text>
                      ) : (
                        <Flex gap={2} flexDirection="column" mb={2}>
                          <Text textStyle="textBody">
                            No, the guidelines are not applicable to my study.
                          </Text>
                          {configs.StandardReportingGuidelinesText &&
                            formData.preference
                              .standardReportingGuidelinesText && (
                              <Flex flexDirection="column" gap={1}>
                                <Text textStyle="textBody">
                                  <i>Additional Details: </i>
                                  {
                                    formData.preference
                                      .standardReportingGuidelinesText
                                  }
                                </Text>
                              </Flex>
                            )}
                        </Flex>
                      )}
                      {formData.preference.standardReportingGuidelinesDetails &&
                        formData.preference.standardReportingGuidelinesDetails.map(
                          item => (
                            <Flex
                              direction={'column'}
                              paddingLeft={2}
                              key={item}
                            >
                              <Text textStyle="textBody"> • {item}</Text>
                              {item === 'CONSORT Statement' &&
                              formData.fileSet.CONSORTFiles?.length ? (
                                <Flex
                                  as="ol"
                                  flexDirection="column"
                                  listStyleType="decimal"
                                  listStylePosition="outside"
                                  textStyle="headingItem"
                                  p={0}
                                  m={0}
                                  marginLeft="2rem"
                                  mt={2}
                                >
                                  {formData.fileSet.CONSORTFiles?.map(file => (
                                    <Flex
                                      as={'li'}
                                      key={file.url}
                                      alignItems="center"
                                      paddingY={2}
                                      width="100%"
                                      gap={2}
                                      whiteSpace={'normal'}
                                      wordBreak={'break-word'}
                                      data-cy={`sourcefile-file-${file.name}`}
                                      sx={{
                                        display: 'list-item',
                                        paddingX: '0'
                                      }}
                                    >
                                      <FileViewerButtonText
                                        file={file}
                                        submissionContextId={ctxId}
                                        wordBreak={'break-word'}
                                        textAlign={'start'}
                                        whiteSpace={'normal'}
                                        data-cy={`button-preview-${file.name}`}
                                      >
                                        {file.name}
                                      </FileViewerButtonText>
                                    </Flex>
                                  ))}
                                </Flex>
                              ) : null}
                              {item === 'ARRIVE' &&
                              formData.fileSet.ARRIVEFiles?.length ? (
                                <Flex
                                  as="ol"
                                  flexDirection="column"
                                  listStyleType="decimal"
                                  listStylePosition="outside"
                                  textStyle="headingItem"
                                  p={0}
                                  m={0}
                                  marginLeft="2rem"
                                  mt={2}
                                >
                                  {formData.fileSet.ARRIVEFiles?.map(file => (
                                    <Flex
                                      as={'li'}
                                      key={file.url}
                                      alignItems="center"
                                      paddingY={2}
                                      width="100%"
                                      gap={2}
                                      whiteSpace={'normal'}
                                      wordBreak={'break-word'}
                                      data-cy={`sourcefile-file-${file.name}`}
                                      sx={{
                                        display: 'list-item',
                                        paddingX: '0'
                                      }}
                                    >
                                      <FileViewerButtonText
                                        file={file}
                                        submissionContextId={ctxId}
                                        wordBreak={'break-word'}
                                        textAlign={'start'}
                                        whiteSpace={'normal'}
                                        data-cy={`button-preview-${file.name}`}
                                      >
                                        {file.name}
                                      </FileViewerButtonText>
                                    </Flex>
                                  ))}
                                </Flex>
                              ) : null}
                            </Flex>
                          )
                        )}
                      {formData.fileSet.StandardReportingGuidelinesFileDetails
                        ?.length ? (
                        <Flex
                          as="ol"
                          flexDirection="column"
                          listStyleType="decimal"
                          listStylePosition="outside"
                          textStyle="headingItem"
                          p={0}
                          m={0}
                          marginLeft="1rem"
                          mt={2}
                        >
                          {formData.fileSet.StandardReportingGuidelinesFileDetails.map(
                            file => (
                              <Flex
                                as={'li'}
                                key={file.url}
                                alignItems="center"
                                paddingY={2}
                                width="100%"
                                gap={2}
                                whiteSpace={'normal'}
                                wordBreak={'break-word'}
                                data-cy={`sourcefile-file-${file.name}`}
                                sx={{ display: 'list-item', paddingX: '0' }}
                              >
                                <FileViewerButtonText
                                  file={file}
                                  submissionContextId={ctxId}
                                  wordBreak={'break-word'}
                                  textAlign={'start'}
                                  whiteSpace={'normal'}
                                  data-cy={`button-preview-${file.name}`}
                                >
                                  {file.name}
                                </FileViewerButtonText>
                              </Flex>
                            )
                          )}
                        </Flex>
                      ) : null}
                    </Flex>
                  )}

                  {configs.SupplementaryUpload && (
                    <Flex gap={2} flexDirection="column" width={'full'}>
                      <FieldLabel
                        isRequired={
                          configs.SupplementaryUpload.params.validations
                            .required
                        }
                        label={`${configs.SupplementaryUpload.params.labels.main} (${supplementaryFileCount})`}
                        subText=""
                        data-cy="supplementary-files-title-text"
                      />
                      {formData.fileSet.Supplementary?.length ? (
                        <Flex
                          as="ol"
                          flexDirection="column"
                          listStyleType="decimal"
                          listStylePosition="outside"
                          textStyle="headingItem"
                          p={0}
                          m={0}
                          marginLeft="1rem"
                        >
                          {formData.fileSet.Supplementary?.map(file => (
                            <Flex
                              key={file.url}
                              alignItems="center"
                              paddingY={2}
                              as={'li'}
                              width="100%"
                              sx={{ display: 'list-item', paddingX: '0' }}
                              gap={2}
                              data-cy={`supplementary-file-${file.name}`}
                            >
                              <FileViewerButtonText
                                file={file}
                                submissionContextId={ctxId}
                                wordBreak={'break-word'}
                                textAlign={'start'}
                                whiteSpace={'normal'}
                                data-cy={`button-preview-${file.name}`}
                              >
                                {file.name}
                              </FileViewerButtonText>
                            </Flex>
                          ))}
                        </Flex>
                      ) : (
                        <Text textStyle="textBody" color="GrayText">
                          No data available
                        </Text>
                      )}
                    </Flex>
                  )}

                  {configs.AdditionalSupplementary && (
                    <Flex gap={2} flexDirection="column" width={'full'}>
                      <FieldLabel
                        isRequired={
                          configs.AdditionalSupplementary.params.validations
                            .required
                        }
                        label={`${configs.AdditionalSupplementary.params.labels.main} (${additionalSupplementaryFileCount})`}
                        subText=""
                        data-cy="additional-supplementary-files-title-text"
                      />
                      {formData.fileSet.AdditionalSupplementary?.length ? (
                        <Flex
                          as="ol"
                          flexDirection="column"
                          listStyleType="decimal"
                          listStylePosition="outside"
                          textStyle="headingItem"
                          p={0}
                          m={0}
                          marginLeft="1rem"
                        >
                          {formData.fileSet.AdditionalSupplementary.map(
                            file => (
                              <Flex
                                as={'li'}
                                key={file.url}
                                alignItems="center"
                                paddingY={2}
                                width="100%"
                                gap={2}
                                data-cy={`additional-supplementary-file-${file.name}`}
                                sx={{ display: 'list-item', paddingX: '0' }}
                              >
                                <FileViewerButtonText
                                  file={file}
                                  submissionContextId={ctxId}
                                  wordBreak={'break-word'}
                                  textAlign={'start'}
                                  whiteSpace={'normal'}
                                  data-cy={`button-preview-${file.name}`}
                                >
                                  {file.name}
                                </FileViewerButtonText>
                              </Flex>
                            )
                          )}
                        </Flex>
                      ) : (
                        <Text textStyle="textBody" color="GrayText">
                          No data available
                        </Text>
                      )}
                    </Flex>
                  )}
                  {configs.CoverLetter && (
                    <Flex
                      gap={2}
                      flexDirection="column"
                      data-cy="cover-letter-section"
                    >
                      <FieldLabel
                        isRequired={
                          configs.CoverLetter.params.validations.required
                        }
                        label={configs.CoverLetter.params.labels.main}
                        subText=""
                        data-cy="additional-supplementary-files-title-text"
                      />
                      {formData.fileSet.CoverLetter ? (
                        <Flex alignItems="center" width="100%" gap={2}>
                          {formData.fileSet?.CoverLetter && (
                            <FileViewerButtonText
                              file={formData.fileSet?.CoverLetter}
                              submissionContextId={ctxId}
                              wordBreak={'break-word'}
                              textAlign={'start'}
                              whiteSpace={'normal'}
                              data-cy={`button-preview-${formData.fileSet?.CoverLetter?.name}`}
                              disabled={!formData.fileSet?.CoverLetter?.url}
                            >
                              {formData.fileSet?.CoverLetter?.name}
                            </FileViewerButtonText>
                          )}
                        </Flex>
                      ) : (
                        <Text textStyle="textBody" color="GrayText">
                          No data available
                        </Text>
                      )}
                    </Flex>
                  )}
                  {configs.Figures && (
                    <>
                      <Flex flexDirection="column" width="100%" gap={2}>
                        <Text textStyle="headingItem">
                          Figures ({figures.length})
                        </Text>

                        {!hasFigures ? (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        ) : (
                          <FiguresDisplay figures={figures} />
                        )}
                      </Flex>
                    </>
                  )}
                  {configs.GraphicalAbstractImage && (
                    <Flex flexDirection="column" width="100%" gap={2}>
                      <Text textStyle="headingItem">
                        {configs.GraphicalAbstractImage.params.labels.main} (
                        {graphicalAbstractImages.length})
                      </Text>
                      {!hasGraphicalAbstractImages ? (
                        <Text textStyle="textBody" color="GrayText">
                          No data available
                        </Text>
                      ) : (
                        <GraphicalAbstractImageDisplay
                          images={graphicalAbstractImages}
                        />
                      )}
                    </Flex>
                  )}
                  {configs.Tables && (
                    <>
                      <Flex flexDirection="column" width="100%" gap={2}>
                        <Text textStyle="headingItem">
                          Tables {hasTables && `(${tables.length})`}
                        </Text>

                        {!hasTables ? (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        ) : (
                          <TablesDisplay tables={tables} />
                        )}
                      </Flex>
                    </>
                  )}
                  <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                    gap={4}
                  >
                    {configs.NumberOfFigures && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.NumberOfFigures.params.validations.required
                          }
                          label={configs.NumberOfFigures.params.labels.main}
                          subText=""
                        />
                        {formData?.preference?.numberOfFigures !== null &&
                        formData?.preference?.numberOfFigures !== undefined ? (
                          <Text
                            textStyle="textBody"
                            width={'100%'}
                            data-cy="numberOfFigures"
                          >
                            {formData.preference.numberOfFigures}
                          </Text>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                    {configs.NumberOfColorFigures && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.NumberOfColorFigures.params.validations
                              .required
                          }
                          label={
                            configs.NumberOfColorFigures.params.labels.main
                          }
                          subText=""
                        />
                        {formData?.preference?.numberOfColorFigures !== null &&
                        formData?.preference?.numberOfColorFigures !==
                          undefined ? (
                          <Text
                            textStyle="textBody"
                            width={'100%'}
                            data-cy="numberOfColorFigures"
                          >
                            {formData.preference.numberOfColorFigures}
                          </Text>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                    {configs.NumberOfTables && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.NumberOfTables.params.validations.required
                          }
                          label={configs.NumberOfTables.params.labels.main}
                          subText=""
                        />
                        {formData?.preference?.numberOfTables !== null &&
                        formData?.preference?.numberOfTables !== undefined ? (
                          <Text
                            textStyle="textBody"
                            width={'100%'}
                            data-cy="numberOfTables"
                          >
                            {formData.preference.numberOfTables}
                          </Text>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                    {configs.NumberOfWords && (
                      <Flex flexDirection="column" gap={2}>
                        <FieldLabel
                          isRequired={
                            configs.NumberOfWords.params.validations.required
                          }
                          label={configs.NumberOfWords.params.labels.main}
                          subText=""
                        />
                        {formData?.preference?.numberOfWords !== null &&
                        formData?.preference?.numberOfWords !== undefined ? (
                          <Text
                            textStyle="textBody"
                            width={'100%'}
                            data-cy="numberOfWords"
                          >
                            {formData.preference.numberOfWords}
                          </Text>
                        ) : (
                          <Text textStyle="textBody" color="GrayText">
                            No data available
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Grid>

                  {configs.GeneralClassifications && (
                    <Flex flexDirection="column" gap={2}>
                      <FieldLabel
                        label={
                          configs.GeneralClassifications.params.labels.main
                        }
                        isRequired={
                          configs.GeneralClassifications.params.validations
                            .required
                        }
                        subText=""
                      />
                      {formData.preference.generalClassifications?.length ? (
                        <Flex flexDirection="column" gap={1}>
                          {formData.preference.generalClassifications.map(
                            (value, index) => (
                              <Text key={index} textStyle="textBody">
                                •{' '}
                                {configs.GeneralClassifications?.params.labels
                                  .options[value]?.main || value}
                              </Text>
                            )
                          )}
                        </Flex>
                      ) : (
                        <Text textStyle="textBody" color="GrayText">
                          No data available
                        </Text>
                      )}
                    </Flex>
                  )}
                </Flex>
              </OutlineCard>
            </>
          )}

          {/* ----- Authors ----- */}
          {(configs.Authors ||
            configs.SubmittingAuthor ||
            configs.CorrespondingAuthor ||
            configs.ContactAuthor) && (
            <>
              <Flex>
                <Text
                  textStyle="headingGroup"
                  data-cy="author-information-title-text"
                  id={authorInformationTitle}
                >
                  {authorInformationTitle} ({formData.metadata.authors.length})
                </Text>
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label="Confirms all author metadata, affiliations, ORCID iDs, and CRediT contributions have been provided and are ready for publication"
                  borderRadius={4}
                  data-cy="contributions-info-tooltip"
                >
                  <ButtonIcon
                    size="small"
                    ariaLabel="Back to workspace settings"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    data-cy="contributions-info-icon"
                  />
                </Tooltip>
              </Flex>

              <OutlineCard width="100%">
                {/* Mobile: card layout */}
                <Flex
                  display={{ base: 'flex', md: 'none' }}
                  flexDirection="column"
                  gap={3}
                  padding={3}
                >
                  {formData.metadata.authors.map((author, index) =>
                    renderAuthorCard(author, index)
                  )}
                </Flex>

                {/* Desktop: table layout */}
                <Flex
                  display={{ base: 'none', md: 'flex' }}
                  flexDirection="column"
                  padding={2}
                >
                  <Table data-cy="author-table">
                    <Table.HeaderRow
                      display="flex"
                      alignItems="center"
                      data-cy="author-table-header"
                    >
                      <Table.HeaderCell width="80px">S.No.</Table.HeaderCell>
                      <Table.HeaderCell flex={1} data-cy="author-header-cell">
                        Author
                      </Table.HeaderCell>

                      {affiliationField !== 'hidden' && (
                        <Table.HeaderCell
                          flex={1}
                          data-cy="affiliation-header-cell"
                        >
                          Affiliation
                        </Table.HeaderCell>
                      )}

                      {contributionsField !== 'hidden' && (
                        <Table.HeaderCell
                          flex={1}
                          padding={2}
                          alignItems="center"
                          data-cy="contributions-header-cell"
                        >
                          CRediT Contributions
                          <Tooltip
                            hasArrow
                            shouldWrapChildren
                            placement="top"
                            label="Contributor Role Taxonomy (CRediT) Contributions"
                            borderRadius={4}
                            data-cy="contributions-info-tooltip"
                          >
                            <ButtonIcon
                              size="small"
                              ariaLabel="Back to workspace settings"
                              icon={<FontAwesomeIcon icon={faInfoCircle} />}
                              data-cy="contributions-info-icon"
                            />
                          </Tooltip>
                        </Table.HeaderCell>
                      )}
                    </Table.HeaderRow>

                    <Table.Body data-cy="author-table-body">
                      {formData.metadata.authors.map((author, index) => (
                        <React.Fragment key={index}>
                          {renderAuthorTableRow(author, index)}
                        </React.Fragment>
                      ))}
                    </Table.Body>
                  </Table>
                </Flex>
              </OutlineCard>
            </>
          )}

          {/* ----- Preferences ----- */}
          {hasSubmissionPreferencesData && (
            <Flex>
              <Text
                textStyle="headingGroup"
                data-cy="submission-preferences-title-text"
                id={submissionPreferencesTitle}
              >
                {submissionPreferencesTitle}
              </Text>
              <Tooltip
                hasArrow
                shouldWrapChildren
                placement="top"
                label="Confirms all mandatory declarations have been completed, including reviewer suggestions, competing interests, funding, and prior submission status"
                borderRadius={4}
                data-cy="contributions-info-tooltip"
              >
                <ButtonIcon
                  size="small"
                  ariaLabel="Back to workspace settings"
                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  data-cy="contributions-info-icon"
                />
              </Tooltip>
            </Flex>
          )}

          {configs.CommentForEditor && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.CommentForEditor.params.validations.required
                  }
                  label={configs.CommentForEditor.params.labels.main}
                  subText=""
                  data-cy="comment-for-editor-text"
                />
                <Box>
                  {formData.preference.commentForEditor ? (
                    <RichTextPreview
                      value={formData.preference.commentForEditor}
                      data-cy="preview-comment-for-editor"
                    />
                  ) : (
                    <Text textStyle="textBody" color="GrayText">
                      No data available
                    </Text>
                  )}
                </Box>
              </Flex>
            </OutlineCard>
          )}

          {configs.CoAuthorApproval && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.CoAuthorApproval.params.validations.required
                  }
                  label={configs.CoAuthorApproval.params.labels.subtitle}
                  subText=""
                />
                {formData.preference.coAuthorApproval !== null &&
                formData.preference.coAuthorApproval !== undefined ? (
                  <Text
                    textStyle="textBody"
                    width={'100%'}
                    data-cy="co-author-approval"
                  >
                    {configs.CoAuthorApproval.params.labels.options[
                      formData.preference.coAuthorApproval
                    ]?.main ?? formData.preference.coAuthorApproval}
                  </Text>
                ) : (
                  <Text textStyle="textBody" color="GrayText">
                    No data available
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {/* Data Availability */}
          {configs.DataAvailability && (
            <>
              <Flex flexDirection="column" gap={4}>
                <FieldLabel
                  isRequired={
                    configs.DataAvailability.params.validations.required
                  }
                  label="Data Availability"
                  subText=""
                  data-cy="data-availability-title-text"
                />
                {Divider}
              </Flex>

              {(() => {
                const {
                  dataUnavailabilityReason,
                  dataAvailabilityOpenlyAvailableUrl,
                  dataAvailabilityEmbargoUrl,
                  dataAvailabilityEmbargoDate
                } = formData.preference || {};

                const status = formData.preference.dataAvailability?.status;

                const label =
                  configs.DataAvailability.params.labels.options?.[status || '']
                    ?.main;

                const unAvailabilityReson =
                  configs.DataAvailability?.params.labels.dependentOptions?.[
                    dataUnavailabilityReason || ''
                  ]?.main;

                switch (status) {
                  case 'not-available':
                    return (
                      dataUnavailabilityReason && (
                        <Banner
                          variant="attention"
                          heading={dataUnavailabilityReason}
                          content={unAvailabilityReson}
                        />
                      )
                    );

                  case 'openly-available':
                    return (
                      dataAvailabilityOpenlyAvailableUrl && (
                        <Flex flexDirection="column" gap={2}>
                          <Text textStyle="headingLine">{label}</Text>
                          <Text textStyle="headingLine">DOI</Text>
                          <Text textStyle="textBody">
                            {dataAvailabilityOpenlyAvailableUrl}
                          </Text>
                        </Flex>
                      )
                    );

                  case 'embargo-period':
                    return (
                      dataAvailabilityEmbargoUrl &&
                      dataAvailabilityEmbargoDate && (
                        <Flex flexDirection="column" gap={2}>
                          <Text textStyle="headingLine">{label}</Text>
                          <Text textStyle="headingLine">DOI</Text>
                          <Text textStyle="textBody">
                            {dataAvailabilityEmbargoUrl}
                          </Text>
                          <Text textStyle="headingLine">Embargo End Date</Text>
                          <Text textStyle="textBody">
                            {format(
                              new Date(dataAvailabilityEmbargoDate),
                              'MM-dd-yyyy'
                            )}
                          </Text>
                        </Flex>
                      )
                    );

                  case 'included-in-article':
                    return (
                      <Flex flexDirection="column" gap={2}>
                        <Text textStyle="headingLine">{label}</Text>
                      </Flex>
                    );

                  default:
                    return null;
                }
              })()}
            </>
          )}

          {configs.DataAvailabilityStatement && (
            <OutlineCard width="100%">
              <Flex padding={4} gap={2} flexDirection="column">
                <FieldLabel
                  isRequired={
                    configs.DataAvailabilityStatement.params.validations
                      .required
                  }
                  label={configs.DataAvailabilityStatement.params.labels.main}
                  subText=""
                />
                {formData.preference.dataAvailabilityStatement?.length ? (
                  <Flex flexDirection="column" gap={3}>
                    {formData.preference.dataAvailabilityStatement.map(item => (
                      <Flex key={item} flexDirection="column" gap={1}>
                        <Text textStyle="textBody">
                          •{' '}
                          {
                            configs.DataAvailabilityStatement?.params.labels
                              .options?.[item]?.main
                          }
                        </Text>
                      </Flex>
                    ))}
                    {configs.DataAvailabilityText &&
                      formData.preference.dataAvailabilityText && (
                        <Flex flexDirection="column" gap={1}>
                          <Text textStyle="textBody">
                            <i>Additional Details: </i>
                            {formData.preference.dataAvailabilityText}
                          </Text>
                        </Flex>
                      )}
                  </Flex>
                ) : (
                  <Text textStyle="textBody" color="GrayText">
                    No data available
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {/* ----- Novelty Statement ----- */}
          {configs.NoveltyStatement && formData.metadata.noveltyStatement && (
            <>
              <Flex flexDirection="column" gap={4}>
                <Text
                  textStyle="headingOverline"
                  textTransform="uppercase"
                  data-cy="motivation-to-publish-title-text"
                >
                  Motivation to publish
                </Text>
                {Divider}
              </Flex>
              {configs.NoveltyStatement &&
                formData.metadata.noveltyStatement && (
                  <Flex flexDirection="column" gap={2}>
                    <Text
                      textStyle="headingLine"
                      data-cy="novelty-statement-title-text"
                    >
                      {configs.NoveltyStatement.params.labels.main}
                    </Text>
                    <Text textStyle="textBody" data-cy="novelty-statement">
                      {formData.metadata.noveltyStatement}
                    </Text>
                  </Flex>
                )}
            </>
          )}

          {/* ----- Reproduced Works ----- */}
          {configs.CoverLetter || configs.FiguresAndTables ? (
            <>
              {configs.FiguresAndTables && (
                <Flex
                  flexDirection="column"
                  gap={6}
                  data-cy="figures-and-tables-section"
                >
                  <Flex flexDirection="column" gap={4}>
                    <Text textStyle="headingOverline" textTransform="uppercase">
                      Licenses and permissions
                    </Text>
                    {Divider}
                  </Flex>
                  {formData.fileSet.FiguresAndTables?.value?.length ? (
                    <FiguresAndTablesDisplay
                      value={formData.fileSet.FiguresAndTables.value}
                      fieldConfig={configs.FiguresAndTables}
                      data-cy="figures-and-tables-display"
                    />
                  ) : (
                    <FileListEmptyState
                      fieldLabels={configs.FiguresAndTables.params.labels}
                    />
                  )}
                </Flex>
              )}
            </>
          ) : null}

          {/* ---- Licenses and Permissions ----- */}
          {configs.LicensesAndPermissions ||
          configs.LicensesAndPermissionsReproducedContent ||
          configs.LicensesAndPermissionsFiles ? (
            <>
              <Flex flexDirection="column" gap={4}>
                <Text
                  textStyle="headingOverline"
                  textTransform="uppercase"
                  data-cy="licenses-and-permissions-title"
                >
                  Licenses and Permissions
                </Text>
                {Divider}
              </Flex>

              {/* Licenses and Permissions */}
              {configs.LicensesAndPermissions && (
                <Flex gap={2} flexDirection="column">
                  <Text
                    textStyle="headingItem"
                    data-cy="licenses-and-permissions-field-label"
                  >
                    {configs.LicensesAndPermissions.params.labels.input?.author}
                  </Text>
                  <Text
                    textStyle="textBody"
                    data-cy="licenses-and-permissions-value"
                  >
                    {formData.metadata.LicensesAndPermissions === 'yes'
                      ? 'Yes'
                      : 'No'}
                  </Text>
                </Flex>
              )}

              {/* Licenses and Permissions - Reproduced content */}
              {configs.LicensesAndPermissionsReproducedContent &&
                reproducedContentExternalDependency?.value ===
                  formData.metadata.LicensesAndPermissions && (
                  <Flex gap={2} flexDirection="column">
                    <Text
                      textStyle="headingItem"
                      data-cy="licenses-and-permissions-reproduced-content-field-label"
                    >
                      {
                        configs.LicensesAndPermissionsReproducedContent.params
                          .labels.main
                      }
                    </Text>
                    <Text
                      textStyle="textBody"
                      data-cy="licenses-and-permissions-reproduced-content-value"
                    >
                      {
                        formData.metadata
                          .LicensesAndPermissionsReproducedContent
                      }
                    </Text>
                  </Flex>
                )}

              {/* Licenses and Permissions - Permission Files */}
              {configs.LicensesAndPermissionsFiles &&
                permissionFilesExternalDependency?.value ===
                  formData.metadata.LicensesAndPermissions && (
                  <Flex gap={2} flexDirection="column">
                    <Text
                      textStyle="headingItem"
                      data-cy="licenses-and-permissions-files-label"
                    >
                      {configs.LicensesAndPermissionsFiles.params.labels.main}
                    </Text>
                    <Flex flexDirection="column">
                      {formData.fileSet.LicensesAndPermissionsFiles?.map(
                        file => (
                          <Flex
                            key={file.url}
                            alignItems="center"
                            paddingY={4}
                            width="100%"
                            height="60px"
                            gap={2}
                            data-cy={`licenses-and-permissions-files-value-${file.name}`}
                          >
                            <FontAwesomeIcon fontSize="20px" icon={faFile} />

                            <ButtonText
                              onClick={() => openFile(file.url)}
                              data-cy={`button-preview-${file.name}`}
                            >
                              {file.name}
                            </ButtonText>
                          </Flex>
                        )
                      )}
                    </Flex>
                  </Flex>
                )}
            </>
          ) : null}

          {/* ----- Peer Review ----- */}
          {(configs.TransparentPeerReview ||
            configs.DoubleAnonymousPeerReview ||
            configs.RecommendedReviewers ||
            configs.OpposedReviewers) && (
            <>
              {configs.DoubleAnonymousPeerReview && (
                <Flex gap={2} flexDirection="column">
                  <FieldLabel
                    isRequired={
                      configs.DoubleAnonymousPeerReview.params.validations
                        .required
                    }
                    label="Double Anonymous Peer Review"
                    subText=""
                    data-cy="double-anonymous-peer-review-title-text"
                  />
                  <Text
                    textStyle="textBody"
                    data-cy="double-anonymous-peer-review-selection"
                  >
                    {formData.preference.doubleAnonymousPeerReview
                      ? configs.DoubleAnonymousPeerReview.params.labels.options[
                          'true'
                        ]?.main
                      : configs.DoubleAnonymousPeerReview.params.labels.options[
                          'false'
                        ]?.main}
                  </Text>
                </Flex>
              )}

              {/* Transparent Peer Review */}
              {configs.TransparentPeerReview && (
                <OutlineCard width="100%">
                  <Flex gap={2} flexDirection="column" padding={4}>
                    <FieldLabel
                      isRequired={
                        configs.TransparentPeerReview.params.validations
                          .required
                      }
                      label="Transparent Peer Review"
                      subText=""
                      data-cy="transparent-peer-review-title-text"
                    />
                    <Text
                      textStyle="textBody"
                      data-cy="transparent-peer-review-selection"
                    >
                      {formData.preference.transparentPeerReview
                        ? configs.TransparentPeerReview.params.labels.options[
                            'true'
                          ]?.main
                        : configs.TransparentPeerReview.params.labels.options[
                            'false'
                          ]?.main}
                    </Text>
                  </Flex>
                </OutlineCard>
              )}

              {configs.RecommendedReviewers && (
                <OutlineCard width="100%">
                  <Flex flexDirection="column" gap={2} padding={4}>
                    <FieldLabel
                      isRequired={
                        configs.RecommendedReviewers.params.validations.required
                      }
                      label={
                        configs.RecommendedReviewers.params.labels.subtitle
                      }
                      subText=""
                      data-cy="recommended-reviewers-title-text"
                    />

                    {formData.preference.recommendedReviewers?.exists ? (
                      <>
                        <Text textStyle="textBody">Yes</Text>

                        {/* Mobile: card layout */}
                        <Flex
                          display={{ base: 'flex', md: 'none' }}
                          flexDirection="column"
                          gap={3}
                        >
                          {formData.preference.recommendedReviewers.value.map(
                            (reviewer, index) =>
                              renderReviewerCard(reviewer, index)
                          )}
                        </Flex>

                        {/* Desktop: table layout */}
                        <Box display={{ base: 'none', md: 'block' }}>
                          <Table>
                            <Table.HeaderRow display="flex" alignItems="center">
                              <Table.HeaderCell width="80px">
                                S.No.
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Reviewer
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Affiliation
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Reason for Recommendation
                              </Table.HeaderCell>
                            </Table.HeaderRow>
                            <Table.Body>
                              {formData.preference.recommendedReviewers.value.map(
                                (reviewer, index) => (
                                  <Table.Row
                                    alignItems="start"
                                    _last={{ borderBottom: 'none' }}
                                    key={index}
                                    data-cy="grant-row"
                                  >
                                    <Table.RowCell width="80px">
                                      {index + 1}
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      flexDirection="column"
                                    >
                                      <Text textStyle="headingLine">{`${reviewer.firstName} ${reviewer.lastName}`}</Text>
                                      <Text textStyle="textContext">
                                        {reviewer.email}
                                      </Text>
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      display="flex"
                                      flexDirection="column"
                                    >
                                      <Flex
                                        flex={1}
                                        gap={2}
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="flex-start"
                                      >
                                        {reviewer.ringgoldId && (
                                          <RinggoldBadge />
                                        )}
                                        <Text textStyle="textBody">{`${reviewer.affiliation}, ${reviewer.country}`}</Text>
                                      </Flex>
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      alignItems="flex-start"
                                      flexWrap="wrap"
                                    >
                                      <Text textStyle="textBody">
                                        {reviewer.reason}
                                      </Text>
                                    </Table.RowCell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </Box>
                      </>
                    ) : (
                      <Text textStyle="textBody">No</Text>
                    )}
                  </Flex>
                </OutlineCard>
              )}

              {configs.OpposedReviewers && (
                <OutlineCard width="100%">
                  <Flex flexDirection="column" gap={2} padding={4}>
                    <FieldLabel
                      isRequired={
                        configs.OpposedReviewers.params.validations.required
                      }
                      label={configs.OpposedReviewers.params.labels.subtitle}
                      subText=""
                      data-cy="opposed-reviewers-title-text"
                    />

                    {formData.preference.opposedReviewers?.exists ? (
                      <>
                        <Text textStyle="textBody">Yes</Text>

                        {/* Mobile: card layout */}
                        <Flex
                          display={{ base: 'flex', md: 'none' }}
                          flexDirection="column"
                          gap={3}
                        >
                          {formData.preference.opposedReviewers.value.map(
                            (reviewer, index) =>
                              renderReviewerCard(reviewer, index)
                          )}
                        </Flex>

                        {/* Desktop: table layout */}
                        <Box display={{ base: 'none', md: 'block' }}>
                          <Table>
                            <Table.HeaderRow display="flex" alignItems="center">
                              <Table.HeaderCell width="80px">
                                S.No.
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Reviewer
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Affiliation
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Reason for Recommendation
                              </Table.HeaderCell>
                            </Table.HeaderRow>
                            <Table.Body>
                              {formData.preference.opposedReviewers.value.map(
                                (reviewer, index) => (
                                  <Table.Row
                                    alignItems="start"
                                    _last={{ borderBottom: 'none' }}
                                    key={index}
                                    data-cy="grant-row"
                                  >
                                    <Table.RowCell width="80px">
                                      {index + 1}
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      flexDirection="column"
                                    >
                                      <Text textStyle="headingLine">{`${reviewer.firstName} ${reviewer.lastName}`}</Text>
                                      <Text textStyle="textContext">
                                        {reviewer.email}
                                      </Text>
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      display="flex"
                                      flexDirection="column"
                                    >
                                      <Flex
                                        flex={1}
                                        gap={2}
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="flex-start"
                                      >
                                        {reviewer.ringgoldId && (
                                          <RinggoldBadge />
                                        )}
                                        <Text textStyle="textBody">{`${reviewer.affiliation}, ${reviewer.country}`}</Text>
                                      </Flex>
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      alignItems="flex-start"
                                      flexWrap="wrap"
                                    >
                                      <Text textStyle="textBody">
                                        {reviewer.reason}
                                      </Text>
                                    </Table.RowCell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </Box>
                      </>
                    ) : (
                      <Text textStyle="textBody">No</Text>
                    )}
                  </Flex>
                </OutlineCard>
              )}
            </>
          )}

          {/* Clinical Trial */}
          {configs.ClinicalTrials && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.ClinicalTrials.params.validations.required
                  }
                  label={configs.ClinicalTrials.params.labels.subtitle}
                  subText=""
                  data-cy="clinical-trails-title-text"
                />
                {formData.metadata.clinicalTrials?.exists ? (
                  <>
                    <Text textStyle="textBody">Yes</Text>
                    <Text textStyle="textBody">
                      <i>Clinical Trial Registration Number: </i>
                      {formData.metadata.clinicalTrials?.value}
                    </Text>
                  </>
                ) : (
                  <Text textStyle="textBody">No</Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {/* ----- Funding Information ----- */}
          {configs.Funding && !!formData.metadata.funding?.value?.length && (
            <>
              <Flex flexDirection="column" gap={4}>
                <FieldLabel
                  isRequired={configs.Funding.params.validations.required}
                  label="Funding"
                  subText=""
                  data-cy="funding-information-title-text"
                />
                {Divider}
              </Flex>
              <Flex flexDirection="column" gap={6}>
                {formData.metadata.funding.value.map(grant => (
                  <Flex key={grant.grantNumber}>
                    <Flex
                      flex={1}
                      textStyle="headingItem"
                      paddingY={2}
                      data-cy="funding-body"
                    >
                      {grant.fundingBody}
                    </Flex>
                    <Flex
                      flex={2}
                      gap={2}
                      alignItems="flex-start"
                      data-cy="funding-grant-number"
                    >
                      {grant.grantNumber.split(',').map((value, index) => (
                        <Flex key={index}>
                          <Badge>
                            <Text textStyle="headingItem">{value}</Text>
                          </Badge>
                        </Flex>
                      ))}
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </>
          )}

          {configs.FundingGeneric &&
            formData.metadata.fundingGeneric?.exists && (
              <OutlineCard width="100%">
                <Flex flexDirection="column" gap={2} padding={4}>
                  <FieldLabel
                    isRequired={
                      configs.FundingGeneric.params.validations.required
                    }
                    label={configs.FundingGeneric.params.labels.main}
                    subText=""
                    data-cy="publishing-model-sub-title-text"
                  />
                  <Text textStyle="textBody" data-cy="publishing-model">
                    {configs.FundingGeneric?.params.labels.options[
                      formData.metadata.fundingGeneric.exists
                    ]?.main || ''}
                  </Text>
                  {configs.FundingGeneric &&
                    !!formData.metadata.fundingGeneric?.value?.length && (
                      <>
                        {/* Mobile: card layout */}
                        <Flex
                          display={{ base: 'flex', md: 'none' }}
                          flexDirection="column"
                          gap={3}
                        >
                          {formData.metadata.fundingGeneric.value.map(
                            (grant, index) => (
                              <Box
                                key={grant.grantNumber}
                                border="1px solid"
                                borderColor="neutralPrimaryBorder"
                                borderRadius="md"
                                p={3}
                                background="white"
                              >
                                <Text
                                  textStyle="textContext"
                                  color="GrayText"
                                  mb={1}
                                >
                                  #{index + 1}
                                </Text>
                                <Text
                                  textStyle="headingItem"
                                  fontSize="xs"
                                  color="gray.500"
                                  mb={1}
                                >
                                  Funder
                                </Text>
                                <Text textStyle="textBody" mb={2}>
                                  {grant.fundingBody}
                                </Text>
                                <Text
                                  textStyle="headingItem"
                                  fontSize="xs"
                                  color="gray.500"
                                  mb={1}
                                >
                                  Grant or Award Numbers
                                </Text>
                                {grant.grantNumber ? (
                                  <Flex gap={2} flexWrap="wrap">
                                    {grant.grantNumber
                                      .split(',')
                                      .map((value, i) => (
                                        <Badge key={i}>
                                          <Text textStyle="textContext">
                                            {value}
                                          </Text>
                                        </Badge>
                                      ))}
                                  </Flex>
                                ) : (
                                  <Text textStyle="textBody" color="GrayText">
                                    No data available
                                  </Text>
                                )}
                              </Box>
                            )
                          )}
                        </Flex>

                        {/* Desktop: table layout */}
                        <Box display={{ base: 'none', md: 'block' }}>
                          <Table data-cy="grant-table">
                            <Table.HeaderRow
                              display="flex"
                              alignItems="center"
                              data-cy="grant-table-header"
                            >
                              <Table.HeaderCell width="80px">
                                S.No.
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Funder
                              </Table.HeaderCell>
                              <Table.HeaderCell flex={1}>
                                Grant or Award Numbers
                              </Table.HeaderCell>
                            </Table.HeaderRow>
                            <Table.Body data-cy="grant-table-body">
                              {formData.metadata.fundingGeneric.value.map(
                                (grant, index) => (
                                  <Table.Row
                                    alignItems="start"
                                    _last={{ borderBottom: 'none' }}
                                    key={grant.grantNumber}
                                    data-cy="grant-row"
                                  >
                                    <Table.RowCell width="80px">
                                      {index + 1}
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      flexDirection="column"
                                      data-cy="author-cell"
                                    >
                                      {grant.fundingBody}
                                    </Table.RowCell>
                                    <Table.RowCell
                                      flex={1}
                                      gap={2}
                                      display="flex"
                                      alignItems="flex-start"
                                      flexWrap="wrap"
                                    >
                                      {grant.grantNumber ? (
                                        grant.grantNumber
                                          .split(',')
                                          .map((value, index) => (
                                            <Flex key={index}>
                                              <Badge>
                                                <Text textStyle="textContext">
                                                  {value}
                                                </Text>
                                              </Badge>
                                            </Flex>
                                          ))
                                      ) : (
                                        <Text
                                          textStyle="textBody"
                                          color="GrayText"
                                        >
                                          No data available
                                        </Text>
                                      )}
                                    </Table.RowCell>
                                  </Table.Row>
                                )
                              )}
                            </Table.Body>
                          </Table>
                        </Box>
                      </>
                    )}
                </Flex>
              </OutlineCard>
            )}

          {/* Publishing */}
          {(configs.PublishingModel ||
            configs.PreferenceImmediatePublication) && (
            <>
              <Flex flexDirection="column" gap={4}>
                <FieldLabel
                  isRequired={
                    configs.PublishingModel.params.validations.required
                  }
                  label="Publishing"
                  subText=""
                  data-cy="publishing-title-text"
                />
                {Divider}
              </Flex>
              {configs.PublishingModel && (
                <Flex gap={2} flexDirection="column">
                  <FieldLabel
                    isRequired={
                      configs.PublishingModel.params.validations.required
                    }
                    label="Publishing Model"
                    subText=""
                    data-cy="publishing-model-sub-title-text"
                  />
                  <Text textStyle="textBody" data-cy="publishing-model">
                    {publishingModel?.main}
                  </Text>
                </Flex>
              )}
              {formData.preference.openAccessChoice && (
                <Flex gap={2} flexDirection="column">
                  <Text
                    textStyle="headingItem"
                    data-cy="open-access-sub-title-text"
                  >
                    Open Access Choice
                  </Text>
                  <Text textStyle="textBody" data-cy="open-access-choice">
                    {formData.preference.openAccessChoice}
                  </Text>
                </Flex>
              )}
              {formData.preference.openAccessChoiceWorldBankACountry && (
                <Flex
                  gap={2}
                  flexDirection="column"
                  data-cy="world-bank-group-b-country-text"
                >
                  <Text textStyle="headingItem">
                    World Bank Group A Country
                  </Text>
                  <Text
                    textStyle="textBody"
                    data-cy="world-bank-group-b-country"
                  >
                    {formData.preference.openAccessChoiceWorldBankACountry}
                  </Text>
                </Flex>
              )}
              {formData.preference.openAccessChoiceWorldBankBCountry && (
                <Flex
                  gap={2}
                  flexDirection="column"
                  data-cy="world-bank-group-b-country-text"
                >
                  <Text textStyle="headingItem">
                    World Bank Group B Country
                  </Text>
                  <Text
                    textStyle="textBody"
                    data-cy="world-bank-group-b-country"
                  >
                    {formData.preference.openAccessChoiceWorldBankBCountry}
                  </Text>
                </Flex>
              )}
              {formData.preference.openAccessChoiceReviewerDiscountRate && (
                <Flex gap={2} flexDirection="column">
                  <Text
                    textStyle="headingItem"
                    data-cy="open-access-reviewer-discount-id-text"
                  >
                    Open Access Reviewer Discount ID
                  </Text>
                  <Text
                    textStyle="textBody"
                    data-cy="open-access-reviewer-discount-id"
                  >
                    {formData.preference.openAccessChoiceReviewerDiscountRate}
                  </Text>
                </Flex>
              )}
              {/* Immediate Publication */}
              {configs.PreferenceImmediatePublication && (
                <Flex gap={2} flexDirection="column">
                  <FieldLabel
                    isRequired={
                      configs.PreferenceImmediatePublication.params.validations
                        .required
                    }
                    label={
                      configs.PreferenceImmediatePublication.params.labels.main
                    }
                    subText=""
                    data-cy="immediate-publication-consent-title-text"
                  />
                  <Text
                    textStyle="textBody"
                    data-cy="immediate-publication-consent"
                  >
                    {formData.preference.immediatePublication
                      ? configs.PreferenceImmediatePublication.params.labels
                          .options['true']?.main
                      : configs.PreferenceImmediatePublication.params.labels
                          .options['false']?.main}
                  </Text>
                </Flex>
              )}
            </>
          )}

          {/* Additional Preferences */}
          {showAdditionalPreferences && (
            <>
              <Flex flexDirection="column" gap={4}>
                <Text
                  textStyle="headingOverline"
                  textTransform="uppercase"
                  data-cy="additional-preferences-title-text"
                >
                  Additional preferences
                </Text>
                {Divider}
              </Flex>
              {showAcceleratedPublication && (
                <Flex gap={2} flexDirection="column">
                  <Text
                    textStyle="headingItem"
                    data-cy="accelerated-publication-title-text"
                  >
                    Accelerated Publication Request
                  </Text>
                  <Text textStyle="textBody" data-cy="accelerated-publication">
                    {formData.preference.acceleratedPublication}
                  </Text>
                </Flex>
              )}
              {showIPEMMembership && (
                <Flex gap={2} flexDirection="column">
                  <FieldLabel
                    isRequired={
                      configs.IPEMMembership?.params.validations.required
                    }
                    label={configs.IPEMMembership?.params.labels.main ?? ''}
                    subText=""
                    data-cy="ipem-membership-title-text"
                  />
                  <Text textStyle="textBody" data-cy="ipem-membership">
                    {formData.preference.ipemMembership}
                  </Text>
                </Flex>
              )}

              {showSubjectArea && (
                <Flex gap={2} flexDirection="column">
                  <Text
                    textStyle="headingItem"
                    data-cy="subject-area-title-text"
                  >
                    {configs.SubjectArea?.params.labels.main}
                  </Text>
                  <Text textStyle="textBody" data-cy="subject-area">
                    {formData.preference.subjectArea}
                  </Text>
                </Flex>
              )}
            </>
          )}

          {configs.PreviouslySubmitted && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.PreviouslySubmitted.params.validations.required
                  }
                  label={configs.PreviouslySubmitted.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.previouslySubmitted && (
                  <Text
                    textStyle="textBody"
                    width={'100%'}
                    data-cy="previously-submitted"
                  >
                    {formData.metadata.previouslySubmitted.exists ? (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">Yes</Text>
                        <Text whiteSpace="normal" wordBreak="break-word">
                          <i>Previous submissions: </i>
                          {formData.metadata.previouslySubmitted?.value}
                        </Text>
                      </Flex>
                    ) : (
                      'No'
                    )}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}
          {configs.ConflictOfInterestForAuthor && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.ConflictOfInterestForAuthor.params.validations
                      .required
                  }
                  label={
                    configs.ConflictOfInterestForAuthor.params.labels.subtitle
                  }
                  subText=""
                />

                {formData.metadata.conflictOfInterestForAuthor && (
                  <Flex direction={'column'} gap={2}>
                    <Text textStyle="textBody" data-cy="conflict-of-interest">
                      {formData.metadata.conflictOfInterestForAuthor.exists ? (
                        <Flex direction={'column'} gap={2}>
                          <Text textStyle="textBody">
                            {
                              configs.ConflictOfInterestForAuthor.params.labels
                                .options?.['true']?.main
                            }
                          </Text>
                          <Text textStyle="textBody">
                            <i>Details: </i>
                            {
                              formData.metadata.conflictOfInterestForAuthor
                                ?.value
                            }
                          </Text>
                        </Flex>
                      ) : (
                        <>
                          {
                            configs.ConflictOfInterestForAuthor.params.labels
                              .options?.['false']?.main
                          }
                        </>
                      )}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </OutlineCard>
          )}

          {configs.AITools && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={configs.AITools.params.validations.required}
                  label={configs.AITools.params.labels.subtitle}
                  subText=""
                />
                <Text textStyle="textBody">
                  {formData.metadata.aiTools?.exists ? 'Yes' : 'No'}
                </Text>

                {!!formData.metadata.aiTools?.value?.length && (
                  <Flex flexDirection="column" gap={2}>
                    {formData.metadata.aiTools.value.map((tool, index) => (
                      <Text key={index} textStyle="textBody">
                        •{' '}
                        {configs.AITools?.params.labels.dependentOptions[tool]
                          ?.main || index}
                      </Text>
                    ))}
                    {!!formData.metadata.aiTools?.description && (
                      <Text textStyle="textBody">
                        <i>Acknowledgement: </i>
                        {formData.metadata.aiTools.description}
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            </OutlineCard>
          )}
          {configs.Preprint && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={configs.Preprint.params.validations.required}
                  label={configs.Preprint.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.preprint && (
                  <Text textStyle="textBody" data-cy="preprint">
                    {formData.metadata.preprint.exists ? (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">Yes</Text>
                        <Flex gap={2}>
                          <i>Preprint link: </i>
                          <Link
                            variant="primary"
                            href={formData.metadata.preprint?.value}
                            isExternal
                          >
                            {formData.metadata?.preprint?.value}
                          </Link>
                        </Flex>
                      </Flex>
                    ) : (
                      'No'
                    )}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}
          {configs.SharedDataset && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={configs.SharedDataset.params.validations.required}
                  label={configs.SharedDataset.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.sharedDataset && (
                  <Text
                    textStyle="textBody"
                    width={'100%'}
                    data-cy="shared-dataset"
                  >
                    {formData.metadata.sharedDataset.exists ? (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">Yes</Text>
                        <Text whiteSpace="normal" wordBreak="break-word">
                          {formData.metadata.sharedDataset?.value}
                        </Text>
                      </Flex>
                    ) : (
                      'No'
                    )}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {configs.GeneratedDataset && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.GeneratedDataset.params.validations.required
                  }
                  label={configs.GeneratedDataset.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.generatedDataset !== undefined && (
                  <Text textStyle="textBody" data-cy="generated-dataset">
                    {formData.metadata.generatedDataset
                      ? configs.GeneratedDataset.params.labels.options['true']
                          ?.main
                      : configs.GeneratedDataset.params.labels.options['false']
                          ?.main}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {configs.UtilizedDataset && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.UtilizedDataset.params.validations.required
                  }
                  label={configs.UtilizedDataset.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.utilizedDataset !== undefined && (
                  <Text textStyle="textBody" data-cy="utilized-dataset">
                    {formData.metadata.utilizedDataset
                      ? configs.UtilizedDataset.params.labels.options['true']
                          ?.main
                      : configs.UtilizedDataset.params.labels.options['false']
                          ?.main}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {configs.EthicalApproval && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.EthicalApproval.params.validations.required
                  }
                  label={configs.EthicalApproval.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.ethicalApproval && (
                  <Text textStyle="textBody" data-cy="ethicalApproval">
                    {formData.metadata.ethicalApproval.exists === true ? (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">Yes</Text>
                        <Flex gap={2}>
                          <i>Ethical Approval: </i>
                          <Text>
                            {formData.metadata?.ethicalApproval?.value}
                          </Text>
                        </Flex>
                      </Flex>
                    ) : (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">No</Text>
                        <Flex gap={2}>
                          <i>Ethical Approval: </i>
                          <Text>
                            {formData.metadata?.ethicalApproval?.value}
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {configs.EthicsApproval && (
            <OutlineCard width="100%">
              <Flex flexDirection="column" gap={2} padding={4}>
                <FieldLabel
                  isRequired={
                    configs.EthicsApproval.params.validations.required
                  }
                  label={configs.EthicsApproval.params.labels.subtitle}
                  subText=""
                />
                {formData.metadata.ethicsApproval && (
                  <Text
                    textStyle="textBody"
                    width={'100%'}
                    data-cy="ethics-approval"
                  >
                    {formData.metadata.ethicsApproval.exists ? (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">No</Text>
                        <Flex gap={2}>
                          <i>Ethics Approval: </i>
                          <Text>
                            {formData.metadata?.ethicsApproval?.value}
                          </Text>
                        </Flex>
                      </Flex>
                    ) : (
                      <Flex direction={'column'} gap={2}>
                        <Text textStyle="textBody">Yes</Text>
                      </Flex>
                    )}
                  </Text>
                )}
              </Flex>
            </OutlineCard>
          )}

          {/* ----- Terms ----- */}
          {(formConfigs.SubmissionAgreementConfirmed ||
            formConfigs.PrivacyPolicyConsentConfirmed ||
            formConfigs.PublicationConsentConfirmed) && (
            <>
              <Text textStyle="headingSection">
                {submissionPreferences?.consentToPublication.mainLabel}
              </Text>
              <OutlineCard borderless width="100%">
                <Flex
                  flexDirection="column"
                  gap={8}
                  padding={{ base: 4, md: 8 }}
                >
                  {formConfigs.SubmissionAgreementConfirmed && (
                    <Flex flexDirection="column" gap={2}>
                      <Checkbox
                        isInvalid={
                          !!errors?.metadata?.submissionAgreementConfirmed
                        }
                        id={
                          formConfigs.SubmissionAgreementConfirmed
                            .caseDataFields.main
                        }
                        {...register(
                          formConfigs.SubmissionAgreementConfirmed
                            .caseDataFields.main
                        )}
                        ariaLabel={
                          formConfigs.SubmissionAgreementConfirmed
                            .caseDataFields.main
                        }
                        data-cy="submission-agreement-confirmed-checkbox"
                      >
                        {parseStringWithLinks(
                          formConfigs.SubmissionAgreementConfirmed.params.labels
                            .main
                        )}
                      </Checkbox>
                      {errors?.metadata?.submissionAgreementConfirmed && (
                        <FormFeedback variant="error">
                          You must consent to this policy to proceed.
                        </FormFeedback>
                      )}
                    </Flex>
                  )}
                  {formConfigs.PrivacyPolicyConsentConfirmed && (
                    <Flex flexDirection="column" gap={2}>
                      <Checkbox
                        isInvalid={
                          !!errors?.metadata?.privacyPolicyConsentConfirmed
                        }
                        id={
                          formConfigs.PrivacyPolicyConsentConfirmed
                            .caseDataFields.main
                        }
                        {...register(
                          formConfigs.PrivacyPolicyConsentConfirmed
                            .caseDataFields.main
                        )}
                        ariaLabel={
                          formConfigs.PrivacyPolicyConsentConfirmed
                            .caseDataFields.main
                        }
                        data-cy="privacy-policy-checkbox"
                      >
                        {parseStringWithLinks(
                          formConfigs.PrivacyPolicyConsentConfirmed.params
                            .labels.main
                        )}
                      </Checkbox>
                      {errors?.metadata?.privacyPolicyConsentConfirmed && (
                        <FormFeedback variant="error">
                          You must consent to this policy to proceed.
                        </FormFeedback>
                      )}
                    </Flex>
                  )}

                  {formConfigs.PublicationConsentConfirmed && (
                    <Flex flexDirection="column" gap={2}>
                      <Checkbox
                        isInvalid={
                          !!errors?.metadata?.publicationConsentConfirmed
                        }
                        id={
                          formConfigs.PublicationConsentConfirmed.caseDataFields
                            .main
                        }
                        {...register(
                          formConfigs.PublicationConsentConfirmed.caseDataFields
                            .main
                        )}
                        ariaLabel={
                          formConfigs.PublicationConsentConfirmed.caseDataFields
                            .main
                        }
                        data-cy="terms-and-condition-checkbox"
                      >
                        {parseStringWithLinks(
                          formConfigs.PublicationConsentConfirmed.params.labels
                            .main
                        )}
                      </Checkbox>
                      {errors?.metadata?.publicationConsentConfirmed && (
                        <FormFeedback variant="error">
                          You must consent to this policy to proceed.
                        </FormFeedback>
                      )}
                    </Flex>
                  )}
                </Flex>
              </OutlineCard>
            </>
          )}
          {submissionContext?.config.enableConsolidatedPdf && (
            <ConsolidatedSubmissionFiles
              fileSet={formData.fileSet}
              hasFileChanges={hasConsolidatedPdfSourceChanges}
              categoryLabels={fileCategoryLabels}
              onGenerate={generateConsolidatedPdf}
              isGenerating={
                consolidatedPdfStatus === 'generating' ||
                consolidatedPdfStatus === 'refreshing'
              }
              generationStatus={consolidatedPdfStatus}
              generationError={consolidatedPdfError}
              onCheckStatus={closeConsolidatedPdfStatus}
              submissionContextId={ctxId}
              organizationId={formData.submissionContext.organizationId}
              isRequiredModalOpen={isConsolidatedPdfRequiredOpen}
              onCloseRequiredModal={() =>
                setIsConsolidatedPdfRequiredOpen(false)
              }
              isAcknowledgementRequiredModalOpen={
                isConsolidatedPdfAcknowledgementRequiredOpen
              }
              onCloseAcknowledgementRequiredModal={() =>
                setIsConsolidatedPdfAcknowledgementRequiredOpen(false)
              }
              onProceedToAcknowledgement={() => {
                setIsConsolidatedPdfAcknowledgementRequiredOpen(false);
                setOpenConsolidatedPdfPreviewForAcknowledgement(true);
              }}
              isAcknowledged={isConsolidatedPdfAcknowledged}
              onAcknowledge={() => setIsConsolidatedPdfAcknowledged(true)}
              openPreviewForAcknowledgement={
                openConsolidatedPdfPreviewForAcknowledgement
              }
              onPreviewOpenedForAcknowledgement={() =>
                setOpenConsolidatedPdfPreviewForAcknowledgement(false)
              }
            />
          )}
        </Flex>
      </SidebarLayout>

      {/* ----- Footer Buttons ----- */}
      <Flex
        padding={{ base: 3, md: 4 }}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex
          width="100%"
          justifyContent="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
        >
          <Button
            variant="secondary"
            onClick={goToPreviousStep}
            iconLeft={<FontAwesomeIcon icon={faChevronLeft} />}
            data-cy="button-back"
            width={{ base: '100%', md: 'auto' }}
          >
            Back
          </Button>
          <Flex
            gap={{ base: 2, md: 4 }}
            direction={{ base: 'column', md: 'row' }}
            width={{ base: '100%', md: 'auto' }}
          >
            {draftSubmissionId && (
              <DeleteDraft
                submissionId={draftSubmissionId}
                submissionContextId={ctxId}
              />
            )}
            <Button
              variant="secondary"
              onClick={() => onSaveDraft()}
              leftIcon={<FontAwesomeIcon icon={faSave} />}
              data-cy="button-save-draft"
              width={{ base: '100%', md: 'auto' }}
            >
              Save Draft
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createSubmissionMutation.isLoading || isNavigating}
              isLoading={createSubmissionMutation.isLoading || isNavigating}
              iconRight={<FontAwesomeIcon icon={faChevronRight} />}
              data-cy="button-submit"
              width={{ base: '100%', md: 'auto' }}
            >
              Submit
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </form>
  );
};

export default PreviewStep;
